import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { api, extractError } from '../lib/api';

export function AssignEmployeeDialog({ open, onOpenChange, defaultDate, defaultEmpId, defaultProjId, clusterIndex = 0, bloeckeFuer, employees, projects, costCenters, onAssigned }) {
  const [empId, setEmpId] = useState(defaultEmpId || '');
  const [projId, setProjId] = useState(defaultProjId || '');
  const [date, setDate] = useState(defaultDate || '');
  const [hours, setHours] = useState(8);
  const [loading, setLoading] = useState(false);
  // '' = keine Frage nötig, sonst 'neu' oder die Nummer eines bestehenden
  // Blocks. Nur bei Sammelprojekten mit schon vorhandenem Block am selben Tag.
  const [blockWahl, setBlockWahl] = useState('');

  React.useEffect(() => { setDate(defaultDate || ''); }, [defaultDate]);
  React.useEffect(() => { setEmpId(defaultEmpId || ''); }, [defaultEmpId]);
  // Projekt vorbelegen, wenn der Dialog aus einem bestehenden Block heraus
  // geöffnet wird ("weiteren Block anlegen").
  React.useEffect(() => { if (open) setProjId(defaultProjId || ''); }, [open, defaultProjId]);
  React.useEffect(() => { if (!open) { setProjId(''); setHours(8); setBlockWahl(''); } }, [open]);

  const submit = async () => {
    if (!empId || !projId || !date) { toast.error('Bitte alle Felder ausfüllen.'); return; }
    const h = Number(String(hours).replace(',', '.'));
    if (isNaN(h) || h <= 0 || h > 24) { toast.error('Stunden müssen zwischen 0 und 24 liegen.'); return; }
    setLoading(true);
    try {
      // Welcher Block? Aus dem Projektkopf heraus steht er fest. Sonst
      // entscheidet die Auswahl – und wo keine Frage nötig war, bleibt es
      // beim ersten Block, also beim Verhalten wie bei jedem anderen Projekt.
      const zielBlock = istWeitererBlock
        ? (clusterIndex || 0)
        : (blockFrageNoetig
            ? (blockWahl === 'neu' ? naechsterFreier : Number(blockWahl) || 0)
            : 0);
      await api.post('/assignments', {
        employee_id: empId, project_id: projId, date, hours: h,
        cluster_index: zielBlock,
      });
      toast.success(`Einsatz angelegt (${h}h).`);
      onAssigned?.();
      onOpenChange(false);
      setEmpId(''); setProjId(''); setHours(8);
    } catch (err) {
      toast.error(extractError(err));
    } finally { setLoading(false); }
  };

  // Vorgegebenes Projekt (aus dem Projektkopf heraus). Dann ist der Dialog
  // ein "weiterer Block" und kein freies Zuweisen.
  const vorgabeProjekt = defaultProjId ? projects.find(p => p.id === defaultProjId) : null;
  const istWeitererBlock = !!vorgabeProjekt;

  // Bestehende Blöcke des gewählten Projekts an diesem Tag.
  const gewaehltesProjekt = projects.find(p => p.id === projId);
  const vorhandeneBloecke = (!istWeitererBlock && projId && date && bloeckeFuer)
    ? bloeckeFuer(projId, date)
    : [];
  // Gefragt wird nur, wenn es tatsächlich zwei Möglichkeiten gibt.
  const blockFrageNoetig = !istWeitererBlock
    && !!gewaehltesProjekt?.allow_multiple_per_day
    && vorhandeneBloecke.length > 0;
  const naechsterFreier = (() => {
    const belegt = new Set(vorhandeneBloecke);
    let n = 0;
    while (belegt.has(n)) n += 1;
    return n;
  })();

  // Voreinstellung: der bestehende Block. Das entspricht dem Verhalten vor
  // der Mehrfach-Funktion und ist die harmlosere der beiden Antworten – ein
  // zu viel angelegter Block fällt eher auf als ein fehlender.
  React.useEffect(() => {
    if (!blockFrageNoetig) { setBlockWahl(''); return; }
    setBlockWahl(String(vorhandeneBloecke[vorhandeneBloecke.length - 1]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockFrageNoetig, projId, date]);

  const activeEmps = employees.filter(e => e.active);
  const activeProjs = projects.filter(p => p.status === 'Aktiv');
  const chosenEmp = activeEmps.find(e => e.id === empId);
  // Suggest projects: home-KSt first, then others
  const sortedProjs = useMemo(() => {
    if (!chosenEmp) return activeProjs;
    return [...activeProjs].sort((a, b) => {
      const ah = a.cost_center_id === chosenEmp.cost_center_id ? 0 : 1;
      const bh = b.cost_center_id === chosenEmp.cost_center_id ? 0 : 1;
      if (ah !== bh) return ah - bh;
      return (a.project_number || a.name).localeCompare(b.project_number || b.name);
    });
    // eslint-disable-next-line
  }, [activeProjs, chosenEmp?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {istWeitererBlock ? `Weiterer Block – ${vorgabeProjekt?.name || 'Projekt'}` : 'Einsatz zuweisen'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Datum</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="assign-date-input"/>
          </div>
          <div className="space-y-1.5">
            <Label>Mitarbeiter</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Wählen..." /></SelectTrigger>
              <SelectContent>
                {activeEmps.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} – {costCenters.find(c => c.id === e.cost_center_id)?.short_code || '?'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Kommt der Dialog aus einem Projektkopf ("weiterer Block"), steht
              das Projekt fest. Es dann noch zur Auswahl anzubieten, wäre eine
              Einladung, versehentlich das falsche zu erwischen – und der Block
              gehörte danach woanders hin. */}
          {istWeitererBlock ? (
            <div className="space-y-1.5">
              <Label>Projekt</Label>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <span className="font-mono">{vorgabeProjekt?.project_number || ''}</span>{' '}
                <span className="font-semibold">{vorgabeProjekt?.name}</span>
                <span className="ml-2 text-xs text-slate-500">· Block #{(clusterIndex || 0) + 1}</span>
              </div>
              <p className="text-xs text-slate-500">
                Eigener Block mit eigener Notiz. Weitere Leute später einfach aus der
                Mitarbeiter-Liste auf diesen Block ziehen.
              </p>
            </div>
          ) : (
          <div className="space-y-1.5">
            <Label>Projekt {chosenEmp && <span className="text-xs text-slate-500">(Heimat-KSt zuerst)</span>}</Label>
            <Select value={projId} onValueChange={setProjId}>
              <SelectTrigger data-testid="assign-project-select"><SelectValue placeholder="Wählen..." /></SelectTrigger>
              <SelectContent>
                {sortedProjs.map(p => {
                  const kst = costCenters.find(c => c.id === p.cost_center_id);
                  const isCross = chosenEmp && chosenEmp.cost_center_id !== p.cost_center_id;
                  return (
                    <SelectItem key={p.id} value={p.id}>
                      {p.project_number ? `#${p.project_number} ` : ''}{p.name} – {kst?.short_code || '?'}
                      {isCross ? ' ↗' : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          )}
          {/* Nur bei Sammelprojekten, die an diesem Tag schon einen Block
              haben. Zwei Antworten, beide tun etwas – deshalb gefragt und
              nicht geraten. */}
          {blockFrageNoetig && (
            <div className="space-y-1.5 rounded-md border border-sky-200 bg-sky-50 p-2.5">
              <Label>Block an diesem Tag</Label>
              <Select value={blockWahl} onValueChange={setBlockWahl}>
                <SelectTrigger data-testid="assign-block-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {vorhandeneBloecke.map(i => (
                    <SelectItem key={i} value={String(i)}>
                      In bestehenden Block #{i + 1}
                    </SelectItem>
                  ))}
                  <SelectItem value="neu">Weiterer Auftrag – neuer Block #{naechsterFreier + 1}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-600">
                Dieses Projekt kann an einem Tag mehrere getrennte Aufträge haben. Jeder Block
                hat eigene Leute und eine eigene Notiz für die Adresse.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Stunden</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number" step="0.05" min="0.25" max="24"
                value={hours}
                onChange={e => setHours(e.target.value)}
                className="w-24"
                data-testid="assign-hours-input"
              />
              <div className="flex gap-1">
                {[2, 4, 6, 8].map(h => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setHours(h)}
                    className={`text-[11px] font-semibold px-2 py-1 rounded border ${Number(hours) === h ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                    data-testid={`assign-hours-quick-${h}`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-500">Standard 8h – bei Teileinsatz anpassen. Mehrere Einsätze pro Tag sind erlaubt.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={loading} data-testid="assign-submit-button">Zuweisen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LendToSchabosDialog({ open, onOpenChange, defaultEmpId, defaultDate, onLent }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  React.useEffect(() => { if (!open) setName(''); }, [open]);

  const submit = async () => {
    if (!name) { toast.error('Projektname angeben.'); return; }
    setLoading(true);
    try {
      await api.post('/assignments/lend-schabos', {
        employee_id: defaultEmpId, date: defaultDate, target_project_name: name,
      });
      toast.success('Mitarbeiter an Schabos verliehen.');
      onLent?.();
      onOpenChange(false);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Mitarbeiter an Schabos verleihen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-slate-600">Datum: <span className="font-mono">{defaultDate}</span></div>
          <div className="space-y-1.5">
            <Label>Projektname bei Schabos</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="z.B. Reparatur Nord" data-testid="schabos-project-input"/>
            <p className="text-xs text-slate-500">Existiert das Projekt "Schabos – &lt;Name&gt;" nicht, wird es automatisch angelegt.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={loading} data-testid="schabos-submit-button">Verleihen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LendToOtherKstDialog({ open, onOpenChange, defaultEmpId, defaultDate, employees, projects, costCenters, onLent }) {
  const [targetKst, setTargetKst] = useState('');
  const [projId, setProjId] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (!open) { setTargetKst(''); setProjId(''); } }, [open]);

  const emp = employees.find(e => e.id === defaultEmpId);
  const homeKstId = emp?.cost_center_id;
  const availableKsts = costCenters.filter(c => !c.deleted && c.type === 'Intern' && c.id !== homeKstId);
  const availableProjs = projects.filter(p => p.cost_center_id === targetKst && p.status === 'Aktiv');

  const submit = async () => {
    if (!targetKst || !projId) { toast.error('Bitte KSt und Projekt wählen.'); return; }
    setLoading(true);
    try {
      await api.post('/assignments/lend-other-kst', {
        employee_id: defaultEmpId, date: defaultDate,
        project_id: projId, target_cost_center_id: targetKst,
      });
      toast.success('Mitarbeiter ausgeliehen.');
      onLent?.();
      onOpenChange(false);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>An andere Kostenstelle verleihen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-slate-600">Datum: <span className="font-mono">{defaultDate}</span></div>
          <div className="space-y-1.5">
            <Label>Ziel-Kostenstelle</Label>
            <Select value={targetKst} onValueChange={(v) => { setTargetKst(v); setProjId(''); }}>
              <SelectTrigger data-testid="lend-target-kst"><SelectValue placeholder="Wählen..."/></SelectTrigger>
              <SelectContent>
                {availableKsts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Projekt</Label>
            <Select value={projId} onValueChange={setProjId} disabled={!targetKst}>
              <SelectTrigger data-testid="lend-target-project"><SelectValue placeholder="Wählen..."/></SelectTrigger>
              <SelectContent>
                {availableProjs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={loading} data-testid="lend-submit-button">Ausleihen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddAbsenceDialog({ open, onOpenChange, defaultEmpId, defaultDate, employees = [], onAdded }) {
  const [type, setType] = useState('Urlaub');
  const [halfDay, setHalfDay] = useState(false);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (!open) { setType('Urlaub'); setHalfDay(false); setNote(''); } }, [open]);

  const emp = employees.find(e => e.id === defaultEmpId);
  const isDeviceEmp = emp?.assignment_context === 'device';
  const availableTypes = isDeviceEmp
    ? ['Urlaub', 'Krankheit', 'Schule', 'Sonstiges']
    : ['Urlaub', 'Krankheit', 'Schule', 'Sonstiges', 'Ersatz Geräte'];

  // Wenn 'Ersatz Geräte' vorausgewählt aber MA ist device → auf Urlaub zurücksetzen
  React.useEffect(() => {
    if (type === 'Ersatz Geräte' && isDeviceEmp) setType('Urlaub');
  }, [isDeviceEmp, type]);

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post('/absences', {
        employee_id: defaultEmpId, date: defaultDate, type, half_day: halfDay, note: note.trim() || null,
      });
      // Ganztägig abwesend heißt: raus aus der Planung. Wenn dabei Einsätze
      // entfernt wurden, muss das in der Meldung stehen.
      const entfernt = r.data?.planung?.entfernt || 0;
      const fahrer = r.data?.planung?.fahrer_frei || 0;
      toast.success('Abwesenheit eingetragen.'
        + (entfernt ? ` ${entfernt} Einsatz${entfernt === 1 ? '' : 'e'} aus der Planung genommen.` : '')
        + (fahrer ? ` ${fahrer} Geräte-Einsatz ohne Fahrer.` : ''));
      onAdded?.();
      onOpenChange(false);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Abwesenheit eintragen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-slate-600">
            Datum: <span className="font-mono">{defaultDate}</span>
            {emp && <span className="ml-2 text-slate-500">· {emp.first_name} {emp.last_name}</span>}
          </div>
          <div className="space-y-1.5">
            <Label>Art</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="absence-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            {isDeviceEmp && (
              <p className="text-[11px] text-slate-500">„Ersatz Geräte" ist nicht verfügbar – dieser Mitarbeiter ist bereits im Geräte-Kontext.</p>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={halfDay} onChange={e => setHalfDay(e.target.checked)} />
            Halbtags
          </label>
          <div className="space-y-1.5">
            <Label>Notiz (optional)</Label>
            <Input value={note} onChange={e => setNote(e.target.value)}
                   placeholder="Grund oder Hinweis" data-testid="absence-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={loading} data-testid="absence-submit-button">Eintragen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditAssignmentDialog({ open, onOpenChange, assignment, projects, costCenters, onUpdated }) {
  const [projId, setProjId] = useState('');
  const [date, setDate] = useState('');
  const [hours, setHours] = useState(8);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (assignment) { setProjId(assignment.project_id); setDate(assignment.date); setHours(assignment.hours ?? 8); }
  }, [assignment]);

  const submit = async () => {
    const h = Number(String(hours).replace(',', '.'));
    if (isNaN(h) || h <= 0 || h > 24) { toast.error('Stunden müssen zwischen 0 und 24 liegen.'); return; }
    setLoading(true);
    try {
      await api.patch(`/assignments/${assignment.id}`, { project_id: projId, date, hours: h });
      toast.success(`Einsatz aktualisiert (${h}h).`);
      onUpdated?.();
      onOpenChange(false);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  if (!assignment) return null;
  const activeProjs = projects.filter(p => p.status === 'Aktiv');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Einsatz bearbeiten</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Datum</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Projekt</Label>
            <Select value={projId} onValueChange={setProjId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {activeProjs.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name} – {costCenters.find(c => c.id === p.cost_center_id)?.short_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Stunden</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number" step="0.05" min="0.25" max="24"
                value={hours}
                onChange={e => setHours(e.target.value)}
                className="w-24"
                data-testid="edit-hours-input"
              />
              <div className="flex gap-1">
                {[2, 4, 6, 8].map(h => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setHours(h)}
                    className={`text-[11px] font-semibold px-2 py-1 rounded border ${Number(hours) === h ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={loading}>Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
